import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useViewAs } from "@/hooks/useViewAs";

export interface FoundingMembership {
  id: string;
  user_id: string;
  status: string;
  display_name: string;
  city: string | null;
  country: string | null;
  country_code: string | null;
  amount: number;
  currency: string;
  current_period_end: string | null;
  founding_joined_at: string | null;
  is_public: boolean;
  cancelled_at: string | null;
}

export function useFoundingMembership(user: User | null) {
  const { role } = useViewAs();
  const [membership, setMembership] = useState<FoundingMembership | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setMembership(null);
      setIsLoading(false);
      return;
    }

    // Admin view-as override.
    if (role === 'member' || role === 'premium') {
      setMembership(null);
      setIsLoading(false);
      return;
    }
    if (role === 'founding') {
      setMembership({
        id: 'view-as-founding',
        user_id: user.id,
        status: 'active',
        display_name: user.user_metadata?.full_name || user.email || 'Founding Member',
        city: null,
        country: null,
        country_code: null,
        amount: 39999,
        currency: 'INR',
        current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        founding_joined_at: new Date().toISOString(),
        is_public: true,
        cancelled_at: null,
      });
      setIsLoading(false);
      return;
    }

    let active = true;
    (async () => {
      const { data } = await supabase
        .from("founding_subscriptions")
        .select(
          "id, user_id, status, display_name, city, country, country_code, amount, currency, current_period_end, founding_joined_at, is_public, cancelled_at"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!active) return;
      setMembership((data as FoundingMembership) || null);
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user, role]);

  const isFoundingMember =
    !!membership &&
    membership.status === "active" &&
    (!membership.current_period_end || new Date(membership.current_period_end) > new Date());

  // "Permanent badge" — once they joined as founding, the badge stays even if cancelled.
  const hasFoundingBadge = !!membership && !!membership.founding_joined_at;

  return { membership, isFoundingMember, hasFoundingBadge, isLoading };
}